'use client';

import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import HLSPlayer from '@/components/HLSPlayer';

interface CameraData {
  id: string;
  name: string;
  streamQuality?: string;
  streamDelay?: number;
  isActive: boolean;
}

interface Overlay {
  id: string;
  type: 'text' | 'image';
  content: string;
  x: number;
  y: number;
  fontSize?: number;
  color?: string;
  width?: number;
}

export default function EmbedPage() {
  const params = useParams();
  const searchParams = useSearchParams();
  const apiKey = params.key as string;
  const autoplay = searchParams.get('autoplay') === 'true';
  const muted = searchParams.get('muted') !== 'false';
  
  const [camera, setCamera] = useState<CameraData | null>(null);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [hlsUrl, setHlsUrl] = useState<string>('');
  const [streamStatus, setStreamStatus] = useState<'idle' | 'starting' | 'active' | 'error'>('idle');
  const [overlays, setOverlays] = useState<Overlay[]>([]);
  const [banned, setBanned] = useState(false);
  const [banReason, setBanReason] = useState<string | null>(null);

  useEffect(() => {
    // JS-seviye frame koruma: izinli domainler varsa parent domain'i kontrol et
    checkFrameProtection();
    verifyAndLoadCamera();
  }, [apiKey]);

  const checkFrameProtection = () => {
    // Eğer iframe içinde değilsek kontrol gereksiz
    if (window.self === window.top) return;

    try {
      // Cross-origin iframe'de parent'a erişim engellenir — bu beklenen davranış
      const parentOrigin = window.parent.location.hostname;
      // Eğer erişebildiysek same-origin — sorun yok
      void parentOrigin;
    } catch {
      // Cross-origin iframe: verify API zaten CSP ile koruyacak,
      // ancak ek olarak document.referrer kontrolü yapabiliriz
      // (CSP desteği olmayan eski tarayıcılar için)
    }
  };

  const loadOverlays = async (cameraId: string) => {
    try {
      const response = await fetch(`/api/cameras/${cameraId}/overlays`);
      if (response.ok) {
        const data = await response.json();
        setOverlays(data.overlays || []);
      }
    } catch (err) {
      console.error('Overlays load error:', err);
    }
  };

  const verifyAndLoadCamera = async () => {
    try {
      setLoading(true);
      setError('');

      const verifyResponse = await fetch(`/api/embed/verify?key=${apiKey}`);
      const verifyData = await verifyResponse.json();

      if (!verifyResponse.ok) {
        if (verifyResponse.status === 403) {
          setError('Bu domain için izin verilmemiş. Yayın sahibiyle iletişime geçin.');
        } else {
          setError(verifyData.error || 'Geçersiz API key');
        }
        setLoading(false);
        return;
      }

      // İzinli domain listesi varsa JS seviyesinde de kontrol et
      if (verifyData.allowedDomains && window.self !== window.top) {
        const parentRef = document.referrer;
        if (parentRef) {
          try {
            const refHost = new URL(parentRef).hostname.toLowerCase();
            if (refHost !== 'localhost' && refHost !== '127.0.0.1') {
              const allowed = verifyData.allowedDomains.some((d: string) =>
                refHost === d || refHost.endsWith('.' + d)
              );
              if (!allowed) {
                setError('Bu domain için izin verilmemiş.');
                setLoading(false);
                return;
              }
            }
          } catch { /* ignore parse errors */ }
        }
      }

      setCamera(verifyData.camera);
      await loadOverlays(verifyData.camera.id);

      if (verifyData.camera.isActive) {
        await startStream(verifyData.camera.id);
      }

      setLoading(false);
    } catch (err) {
      console.error('Verify error:', err);
      setError('Yüklenirken bir hata oluştu');
      setLoading(false);
    }
  };

  const startStream = async (cameraId: string) => {
    try {
      setStreamStatus('starting');
      
      // RTSP URL artık client'a gönderilmiyor — sunucu tarafında başlatılıyor
      const response = await fetch('/api/embed/start-stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cameraId, apiKey })
      });

      if (!response.ok) {
        throw new Error('Stream başlatılamadı');
      }

      const data = await response.json();
      setHlsUrl(data.hlsUrl);
      setStreamStatus('active');
    } catch (err) {
      console.error('Stream start error:', err);
      setStreamStatus('error');
    }
  };

  const handleClose = () => {
    window.close();
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'embed-close' }, '*');
    }
  };

  const CloseButton = () => (
    <button
      onClick={handleClose}
      className="fixed top-3 left-3 z-50 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-all duration-200 backdrop-blur-sm border border-white/20 hover:border-white/40"
      title="Kapat"
    >
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );

  const OverlayElements = () => (
    <>
      {overlays.map(overlay => (
        <div
          key={overlay.id}
          className="absolute select-none pointer-events-none"
          style={{ left: `${overlay.x}%`, top: `${overlay.y}%`, transform: 'translate(-50%, -50%)', zIndex: 20 }}
        >
          {overlay.type === 'text' ? (
            <span
              style={{ fontSize: overlay.fontSize || 16, color: overlay.color || '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 0 2px rgba(0,0,0,0.5)' }}
              className="whitespace-nowrap font-bold"
            >
              {overlay.content}
            </span>
          ) : (
            <img src={overlay.content} alt="" style={{ width: overlay.width || 100 }} className="max-w-none rounded" draggable={false} />
          )}
        </div>
      ))}
    </>
  );

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black">
        <CloseButton />
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black text-white p-4">
        <CloseButton />
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="text-xl font-bold mb-2">Erişim Engellendi</h2>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!camera) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black text-white">
        <CloseButton />
        Kamera bulunamadı
      </div>
    );
  }

  if (banned) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black text-white p-4">
        <CloseButton />
        <div className="text-center">
          <svg className="mx-auto h-16 w-16 text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
          <h2 className="text-2xl font-bold mb-2">Erişim Engellendi</h2>
          <p className="text-gray-400 mb-1">Bu yayına erişiminiz yönetici tarafından engellenmiştir.</p>
          {banReason && <p className="text-gray-500 text-sm">Sebep: {banReason}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center">
      <CloseButton />

      {streamStatus === 'starting' && (
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Yayın başlatılıyor...</p>
        </div>
      )}
      
      {streamStatus === 'active' && hlsUrl && (
        <div className="w-full h-full relative">
          <HLSPlayer 
            src={hlsUrl} 
            autoplay={autoplay}
            muted={muted}
            controls={true}
            liveOnly={true}
            cameraId={camera.id}
            className="w-full h-full"
            onBanned={(reason) => { setBanned(true); setBanReason(reason); }}
          />
          {overlays.length > 0 && (
            <div className="absolute inset-0 pointer-events-none">
              <div className="relative w-full h-full">
                <OverlayElements />
              </div>
            </div>
          )}
        </div>
      )}
      
      {streamStatus === 'error' && (
        <div className="text-white text-center">
          <svg className="mx-auto h-12 w-12 text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>Yayın başlatılamadı</p>
        </div>
      )}
    </div>
  );
}
